/**
 * Navigation & Background & Mobile Menu - Moon Moon Agency
 * Handles navigation, background filters, and mobile menu interactions
 */

/*********************************************************************************************
 * 🎯 NAVIGATION MODULE
 */
const NavigationManager = {
	// Local configuration
	config: {
		selectors: {
			header: 'header',
			megaMenuLink: '[data-megamenulink]',
			menuLink: '[data-menulink]',
			filter: '[data-filter]',
			cartFilter: '[data-drawer-cart]',
			searchContainer: '[data-search-container]',
			headerBurger: '[data-header-burger]',
			menuMobile: '[data-menu-mobile]',
			menuSublinks: '[data-menu-sublinks]'
		},
		classes: {
			active: 'active',
			open: 'mm-open',
			drawerOpen: 'mm-drawer-open',
			stopScrolling: 'stop-scrolling',
			mobileMenuSlided: 'mm-mobile-menu-elem-slided'
		},
		timing: {
			filterHideDelay: 300
		}
	},

	/**
	 * Initialize navigation systems
	 */
	init() {
		this.initMegaMenus();
		this.initBackgroundFilter();
		this.initAccessibilityHandlers();
		this.initMegaMenuAccessibility();
		this.initMobileSubmenuAccessibility();
	},

	/**
	 * Initialize accessibility keyboard handlers for header buttons
	 */
	initAccessibilityHandlers() {
		// Burger menu button
		const burgerBtn = window.getElement('[data-header-burger]');
		if (burgerBtn) {
			burgerBtn.addEventListener('click', () => {
				window.mmHideSearch(true, true);
				window.mmMobileMenu();
				// aria-expanded géré dans MobileMenuManager.toggle()
			});
		}

		// Close button inside mobile menu topbar
		const mobileCloseBtn = window.getElement('[data-mobile-menu-close]');
		if (mobileCloseBtn) {
			mobileCloseBtn.addEventListener('click', () => window.mmCloseMobileMenu());
		}

		// Search toggle buttons (mobile + desktop)
		window.getElements('[data-search-toggle]').forEach((searchBtn) => {
			searchBtn.addEventListener('click', () => {
				window.mmCloseMobileMenu();
				window.mmShowSearch(searchBtn);
			});
		});

		// Cart toggle button
		const cartBtn = window.getElement('[data-cart-toggle]');
		if (cartBtn) {
			cartBtn.addEventListener('click', () => {
				window.mmOpenCart(cartBtn);
				window.mmCloseMobileMenu(true);
				window.mmHideSearch(true);
			});
		}

		// Global Escape key handler (close all overlays)
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				const mobileMenu = window.getElement(this.config.selectors.menuMobile);
				if (mobileMenu?.classList.contains(this.config.classes.open)) {
					window.mmCloseMobileMenu();
					burgerBtn?.focus(); // Return focus to burger button
				}
			}
		});
	},

	/**
	 * Initialize mega menu and submenu positioning
	 */
	initMegaMenus() {
		const header = window.getElement(this.config.selectors.header);
		if (!header) {
			window.log('warn', 'NavigationManager', 'Header not found');
			return;
		}

		const headerHeight = header.offsetHeight;

		// Position mega menus (data-megamenulink)
		window.getElements(this.config.selectors.megaMenuLink).forEach((menuElem) => {
			const megaMenu = menuElem.nextElementSibling;
			if (megaMenu?.hasAttribute('data-megamenu')) {
				megaMenu.style.top = `${headerHeight - 1}px`;
			}
		});

		// Position submenus (data-menulink with nested data-megamenu)
		window.getElements(this.config.selectors.menuLink).forEach((menuElem) => {
			const submenu = menuElem.querySelector('[data-megamenu]');
			if (submenu) {
				submenu.style.top = `${headerHeight - 1}px`;
			}
		});
	},

	/**
	 * Initialize mega menu keyboard accessibility
	 */
	initMegaMenuAccessibility() {
		const megaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		const regularMenuLinks = window.getElements(this.config.selectors.menuLink);

		// Mega menu links (with dropdown)
		megaMenuLinks.forEach((link) => {
			const megaMenu = link.nextElementSibling;
			if (!megaMenu?.hasAttribute('data-megamenu')) return;

			// Chevron button toggles the mega menu — the link itself navigates normally
			const toggle = link.querySelector('[data-megamenutoggle]');
			toggle?.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				this.toggleMegaMenu(link, megaMenu);
			});

			// Keyboard: Arrow Down to open + focus first item
			link.addEventListener('keydown', (e) => {
				if (e.key === 'ArrowDown') {
					e.preventDefault();
					const isOpen = link.getAttribute('aria-expanded') === 'true';
					if (!isOpen) {
						this.openMegaMenu(link, megaMenu);
					}
					// Focus first link in mega menu
					const firstLink = megaMenu.querySelector('a');
					firstLink?.focus();
				}
			});

			// Hover to open (keep existing behavior)
			link.addEventListener('mouseenter', () => {
				// Close other mega menus (not this one)
				const allMegaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
				allMegaMenuLinks.forEach((otherLink) => {
					if (otherLink !== link) {
						otherLink.setAttribute('aria-expanded', 'false');
						const otherMegaMenu = otherLink.nextElementSibling;
						if (otherMegaMenu?.hasAttribute('data-megamenu')) {
							otherMegaMenu.style.display = 'none';
						}
					}
				});
				// Open this mega menu
				this.openMegaMenu(link, megaMenu);
			});
		});

		// Regular menu links and submenus
		regularMenuLinks.forEach((link) => {
			// Only add listener if it's NOT a mega menu link
			if (!link.hasAttribute('data-megamenulink')) {
				const submenu = link.querySelector('[data-megamenu]');

				if (submenu) {
					// This link HAS a submenu - close other mega menus on hover
					link.addEventListener('mouseenter', () => {
						// Close all mega menus (but CSS will handle submenu display)
						const allMegaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
						allMegaMenuLinks.forEach((megaLink) => {
							megaLink.setAttribute('aria-expanded', 'false');
							const megaMenu = megaLink.nextElementSibling;
							if (megaMenu?.hasAttribute('data-megamenu')) {
								megaMenu.style.display = 'none';
							}
						});
						// Show background filter for submenu too
						this.showBackgroundFilter();
					});

					// Hide background filter when leaving submenu
					link.addEventListener('mouseleave', () => {
						this.hideBackgroundFilter();
					});
				} else {
					// This link has NO dropdown - close all menus on hover
					link.addEventListener('mouseenter', () => {
						this.closeAllMegaMenus();
					});
				}
			}
		});

		// Mouse leave header to close all menus
		const header = window.getElement(this.config.selectors.header);
		if (header) {
			header.addEventListener('mouseleave', () => {
				this.closeAllMegaMenus();
			});
		}

		// Escape key closes all mega menus
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				const hasOpenMenu = Array.from(megaMenuLinks).some(
					(link) => link.getAttribute('aria-expanded') === 'true'
				);
				if (hasOpenMenu) {
					this.closeAllMegaMenus();
				}
			}
		});

		// Initialize mega menu product links hover/focus
		this.initMegaMenuProductLinks();
	},

	/**
	 * Initialize mega menu product links (replace inline onmouseenter)
	 */
	initMegaMenuProductLinks() {
		const productLinks = window.getElements('[data-mega-menu-products-link]');

		productLinks.forEach((link) => {
			const hideSelector = link.dataset.hideSelector;
			const showSelector = link.dataset.showSelector;

			// Hover event
			link.addEventListener('mouseenter', () => {
				this.showMegaMenuProducts(hideSelector, showSelector);
			});

			// Focus event (keyboard navigation)
			link.addEventListener('focus', () => {
				this.showMegaMenuProducts(hideSelector, showSelector);
			});
		});
	},

	/**
	 * Toggle mega menu state
	 */
	toggleMegaMenu(link, megaMenu) {
		const isOpen = link.getAttribute('aria-expanded') === 'true';
		if (isOpen) {
			this.closeMegaMenu(link, megaMenu);
		} else {
			this.closeAllMegaMenus(); // Close others first
			this.openMegaMenu(link, megaMenu);
		}
	},

	/**
	 * Open mega menu
	 */
	openMegaMenu(link, megaMenu) {
		link.setAttribute('aria-expanded', 'true');
		link.querySelector('[data-megamenutoggle]')?.setAttribute('aria-expanded', 'true');
		megaMenu.style.display = 'block';
		this.showBackgroundFilter();
	},

	/**
	 * Close mega menu
	 */
	closeMegaMenu(link, megaMenu) {
		link.setAttribute('aria-expanded', 'false');
		link.querySelector('[data-megamenutoggle]')?.setAttribute('aria-expanded', 'false');
		megaMenu.style.display = 'none';
	},

	/**
	 * Close all mega menus
	 */
	closeAllMegaMenus() {
		const allMegaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		allMegaMenuLinks.forEach((link) => {
			link.setAttribute('aria-expanded', 'false');
			link.querySelector('[data-megamenutoggle]')?.setAttribute('aria-expanded', 'false');
			const megaMenu = link.nextElementSibling;
			if (megaMenu?.hasAttribute('data-megamenu')) {
				megaMenu.style.display = 'none';
			}
		});
		this.hideBackgroundFilter();
	},

	/**
	 * Show specific mega menu products
	 */
	showMegaMenuProducts(classElemsToHide, classElemToShow) {
		try {
			window.getElements(classElemsToHide).forEach((elem) => (elem.style.display = 'none'));
			const elemToShow = window.getElement(classElemToShow);
			if (elemToShow) {
				elemToShow.style.display = 'grid';
			}
			window.emit('global:mega-menu-products-switched', { hideSelector: classElemsToHide, showSelector: classElemToShow });
		} catch (error) {
			window.log('error', 'NavigationManager', 'Failed to switch mega menu products', { error: error.message });
		}
	},

	/**
	 * Initialize background filter hover behaviors
	 */
	initBackgroundFilter() {
		const elements = {
			header: window.getElement(this.config.selectors.header),
			searchContainer: window.getElement(this.config.selectors.searchContainer),
			filter: window.getElement(this.config.selectors.filter),
			cart: window.getElement(this.config.selectors.cartFilter)
		};

		if (!elements.header || !elements.filter || !elements.cart) {
			window.log('warn', 'NavigationManager', 'Background filter elements not found');
			return;
		}

		const { header, searchContainer, filter, cart } = elements;
		const megaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		const menuLinks = window.getElements(this.config.selectors.menuLink);

		const toggleFilter = (show) => {
			filter.classList.toggle(this.config.classes.active, show);
			window.emit(`global:background-filter-${show ? 'activated' : 'deactivated'}`, { 
				trigger: show ? 'toggle-on' : 'toggle-off',
				source: 'navigation' 
			});
		};

		// Event listeners
		megaMenuLinks.forEach((link) => 
			link.addEventListener('mouseenter', () => toggleFilter(true))
		);
		
		menuLinks.forEach((link) => 
			link.addEventListener('mouseenter', () => toggleFilter(false))
		);

		header.addEventListener('mouseleave', () => {
			const searchOpen = searchContainer?.classList.contains(this.config.classes.open);
			const cartOpen = cart.classList.contains(this.config.classes.drawerOpen);
			
			if (!searchOpen && !cartOpen) {
				toggleFilter(false);
			}
		});

	},

	/**
	 * Show background filter
	 */
	showBackgroundFilter(zIndex) {
		const filter = window.getElement(this.config.selectors.filter);
		if (!filter) {
			window.log('warn', 'NavigationManager', 'Background filter element not found');
			return;
		}
		
		filter.classList.add(this.config.classes.active);
		if (zIndex) filter.style.zIndex = zIndex;
		window.emit('global:background-filter-activated', { 
			trigger: 'show', 
			zIndex, 
			hasZIndex: !!zIndex 
		});
	},

	/**
	 * Hide background filter
	 */
	hideBackgroundFilter() {
		const filter = window.getElement(this.config.selectors.filter);
		if (!filter) return;
		
		setTimeout(() => filter.style.removeProperty('z-index'), this.config.timing.filterHideDelay);
		filter.classList.remove(this.config.classes.active);
		window.emit('global:background-filter-deactivated', { 
			trigger: 'hide',
			delay: this.config.timing.filterHideDelay 
		});
	},

	/**
	 * Initialize mobile submenu accessibility (replace inline onclick)
	 */
	initMobileSubmenuAccessibility() {
		// Submenu trigger buttons (show next)
		const submenuTriggers = window.getElements('[data-mobile-submenu-trigger]');
		submenuTriggers.forEach((btn) => {
			const targetSelector = btn.dataset.mobileSubmenuTrigger;

			btn.addEventListener('click', () => {
				window.mmMobileMenuShowNext(targetSelector);
				btn.setAttribute('aria-expanded', 'true');
			});
		});

		// Submenu back buttons (show prev)
		const submenuBackBtns = window.getElements('[data-mobile-submenu-back]');
		submenuBackBtns.forEach((btn) => {
			const targetSelector = btn.dataset.mobileSubmenuBack;

			btn.addEventListener('click', () => {
				window.mmMobileMenuShowPrev(targetSelector);
				// Find corresponding trigger and update aria-expanded
				const trigger = window.getElement(`[data-mobile-submenu-trigger="${targetSelector}"]`);
				if (trigger) trigger.setAttribute('aria-expanded', 'false');
			});
		});
	},

	/**
	 * Handle window resize
	 */
	onResize() {
		this.initMegaMenus();
	}
};

/*********************************************************************************************
 * 📱 MOBILE MENU MODULE
 */
const MobileMenuManager = {
	// Reference to NavigationManager config
	config: NavigationManager.config,

	// Focus trap state
	focusTrap: {
		isActive: false,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null,
		triggerElement: null
	},

	/**
	 * Setup focus trap for mobile menu
	 */
	setupFocusTrap(mobileMenu) {
		// Store trigger element to restore focus on close
		this.focusTrap.triggerElement = document.activeElement;

		const focusableElements = mobileMenu.querySelectorAll(
			'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if (focusableElements.length === 0) return;

		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab key to trap focus
		this.focusTrap.handleKeyDown = (e) => {
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		// Focus first element
		this.focusTrap.firstFocusable?.focus();

		window.log('info', 'MobileMenuManager', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;

			// Restore focus to the element that triggered the menu
			if (this.focusTrap.triggerElement) {
				this.focusTrap.triggerElement.focus();
				this.focusTrap.triggerElement = null;
			}

			window.log('info', 'MobileMenuManager', 'Focus trap removed');
		}
	},

	/**
	 * Toggle mobile menu
	 */
	toggle() {
		if (!window.isSmallDesktop()) return;

		const header = window.getElement(this.config.selectors.header);
		const icon = window.getElement(this.config.selectors.headerBurger);
		const mobileMenu = window.getElement(this.config.selectors.menuMobile);
		const mobileMenuSecondary = window.getElement(this.config.selectors.menuSublinks);
		const html = document.documentElement;

		if (!header || !icon || !mobileMenu || !mobileMenuSecondary) {
			window.log('warn', 'MobileMenuManager', 'Required elements not found');
			return;
		}

		const headerHeight = header.offsetHeight;

		[header, icon, mobileMenu].forEach((el) => el.classList.toggle(this.config.classes.open));

		const overlay = window.getElement('[data-mobile-menu-overlay]');
		const isOpen = mobileMenu.classList.contains(this.config.classes.open);

		overlay?.classList.toggle(this.config.classes.open, isOpen);

		// Update burger aria-expanded (centralisé ici pour couvrir tous les cas d'appel)
		icon.setAttribute('aria-expanded', String(isOpen));

		if (isOpen) {
			html.classList.add(this.config.classes.stopScrolling);
			this.setupFocusTrap(mobileMenu);
			// Inert background content for screen readers (WCAG 2.4.3)
			document.querySelectorAll('main, footer').forEach(el => el.inert = true);
		} else {
			html.classList.remove(this.config.classes.stopScrolling);
			this.removeFocusTrap();
			document.querySelectorAll('[inert]').forEach(el => el.inert = false);
		}

		window.emit('global:mobile-menu-toggled', { headerHeight });
	},

	/**
	 * Close mobile menu
	 */
	close(keepFilterActive = false) {
		if (!window.isSmallDesktop()) return;

		const header = window.getElement(this.config.selectors.header);
		const icon = window.getElement(this.config.selectors.headerBurger);
		const mobileMenu = window.getElement(this.config.selectors.menuMobile);
		const html = document.documentElement;

		if (!header || !icon || !mobileMenu) {
			window.log('warn', 'MobileMenuManager', 'Required elements not found for closing');
			return;
		}

		[header, icon, mobileMenu].forEach((el) => el.classList.remove(this.config.classes.open));
		html.classList.remove(this.config.classes.stopScrolling);

		const overlay = window.getElement('[data-mobile-menu-overlay]');
		overlay?.classList.remove(this.config.classes.open);

		// Update burger aria-expanded
		icon.setAttribute('aria-expanded', 'false');

		// Restore background content
		document.querySelectorAll('[inert]').forEach(el => el.inert = false);

		// Remove focus trap when menu closes
		this.removeFocusTrap();

		window.emit('global:mobile-menu-closed', { keepFilterActive });
	},

	/**
	 * Show next sub-menu
	 */
	showNext(elemToShowClass) {
		const element = window.getElement(elemToShowClass);
		if (element) {
			element.classList.add(this.config.classes.mobileMenuSlided);
			window.emit('global:mobile-submenu-next', { selector: elemToShowClass });
		}
	},

	/**
	 * Show previous sub-menu
	 */
	showPrev(elemToShowClass) {
		const element = window.getElement(elemToShowClass);
		if (element) {
			element.classList.remove(this.config.classes.mobileMenuSlided);
			window.emit('global:mobile-submenu-prev', { selector: elemToShowClass });
		}
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.NavigationManager = NavigationManager;
window.MobileMenuManager = MobileMenuManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
// Navigation functions
window.getMegaMenuPosition = () => NavigationManager.initMegaMenus();
window.mmMegaMenuShowProducts = (hide, show) => NavigationManager.showMegaMenuProducts(hide, show);

// Background filter functions
window.showBgFilter = (zIndex) => NavigationManager.showBackgroundFilter(zIndex);
window.hideBgFilter = () => NavigationManager.hideBackgroundFilter();

// Mobile menu functions
window.mmMobileMenu = () => MobileMenuManager.toggle();
window.mmCloseMobileMenu = (keep) => MobileMenuManager.close(keep);
window.mmMobileMenuShowNext = (elem) => MobileMenuManager.showNext(elem);
window.mmMobileMenuShowPrev = (elem) => MobileMenuManager.showPrev(elem);

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	NavigationManager.init();
});

window.addEventListener('resize', window.debounce(() => {
	NavigationManager.onResize();
}, 150));